import { ApiResponseData, client } from "@api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "@store/store";
import {
  MessageWizardQuestion,
  MessageWizardRephraseResult,
  MessageWizardResult,
  MessageWizardTargetType,
} from "./messageWizard.types";
import {
  EmailAnalysisDominantParticipantUnresolved,
  EmailAnalysisDraft,
  EmailAnalysisFeedback,
  EmailAnalysisParticipant,
  EmailAnalysisParticipantSide,
} from "@types";

export const getQuestionnaire = createAsyncThunk<
  {
    questions: MessageWizardQuestion[];
    questionsCountInBranch: number;
    savedResults: {
      questionnaireComplete: boolean;
      questionnaireResults: MessageWizardResult[] | null;
    };
  },
  { branchId: string; platformDraftId: string; platformThreadId: string }
>(
  "messageWizard/getQuestionnaire",
  async (
    { branchId, platformDraftId, platformThreadId },
    { getState, rejectWithValue, fulfillWithValue, signal }
  ) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      const response = await client.get<
        ApiResponseData<{
          questions: MessageWizardQuestion[];
          questionsCountInBranch: number;
          savedResults: {
            questionnaireComplete: boolean;
            questionnaireResults: MessageWizardResult[] | null;
          };
        }>
      >(`/services/orgs/${user?.orgId}/users/${user?.userId}/questionnaire`, {
        signal,
        params: {
          platformThreadId: platformThreadId || "",
          draftPlatformMessageId: platformDraftId || "",
          branchId: branchId || "",
        },
      });

      const { questionsCountInBranch, questions, savedResults, ...rest } = response.data.payload;
      const { questionnaireResults, questionnaireComplete } = savedResults;
      const lastQuestionIndex = questionsCountInBranch - 1;
      const lastQuestionId = questions.find(({ index }) => index === lastQuestionIndex)?.wqId || "";

      return fulfillWithValue({
        ...rest,
        questionsCountInBranch,
        savedResults: {
          questionnaireComplete,
          questionnaireResults: questionnaireComplete
            ? questionnaireResults
            : questionnaireResults.filter(({ questionId }) => questionId !== lastQuestionId),
        },
        questions: questions.map(({ options, ...rest }) => ({
          options: options.filter(
            ({ target }) => target.type !== MessageWizardTargetType.CancelAction
          ),
          ...rest,
        })),
      });
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);

export const saveMessageWizardResults = createAsyncThunk<
  null,
  {
    branchId: string;
    platformDraftId: string;
    platformThreadId: string;
    results: MessageWizardResult[];
    isCompleted: boolean;
  }
>(
  "messageWizard/saveMessageWizardResults",
  async (
    { branchId, platformDraftId, platformThreadId, results, isCompleted },
    { getState, rejectWithValue, fulfillWithValue, signal }
  ) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      await client.post<ApiResponseData<{ success: boolean }>>(
        `/services/orgs/${user?.orgId}/users/${user?.userId}/questionnaire`,
        {
          platformThreadId: platformThreadId || "",
          draftPlatformMessageId: platformDraftId || "",
          questionnaireResults: results,
          questionnaireComplete: isCompleted,
          branchId: branchId || "",
        },
        { signal }
      );

      return fulfillWithValue(null);
    } catch (error) {
      return rejectWithValue(null);
    }
  }
);

export const rephrase = createAsyncThunk<
  MessageWizardRephraseResult,
  {
    currentText: string;
    draft: EmailAnalysisDraft;
    savedResults: MessageWizardResult[];
  }
>(
  "messageWizard/rephrase",
  async (
    { currentText, draft, savedResults },
    { getState, fulfillWithValue, rejectWithValue, signal }
  ) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      const {
        communicationType,
        platformDraftId,
        platformThreadId,
        actualSzScore,
        participants,
        messageReplyToId,
      } = draft || {};

      const requestData = {
        participants,
        communicationType: communicationType || "",
        messageReplyToId: messageReplyToId || "",
        platformThreadId: platformThreadId || "",
        draftPlatformMessageId: platformDraftId || "",
        draftText: currentText || "",
        questionnaireResults: savedResults,
        previousActualSZScore: actualSzScore || null,
      };

      const response = await client.post<
        ApiResponseData<{
          isSuccessful: boolean;
          rephraseResult: {
            newText: string;
            sentiment: { mag: number };
            participants: {
              leftParticipant: EmailAnalysisParticipant;
              rightParticipant: EmailAnalysisParticipant;
            };
            dominantParticipant:
              | EmailAnalysisParticipantSide
              | EmailAnalysisDominantParticipantUnresolved
              | null;
            scoreInBranch: number;
          };
        }>
      >(`/services/orgs/${user?.orgId}/users/${user?.userId}/rephrase`, requestData, { signal });

      const { isSuccessful, rephraseResult } = response.data.payload;

      return fulfillWithValue({
        isSuccessful,
        ...rephraseResult,
      });
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);

export const sendFeedback = createAsyncThunk<
  EmailAnalysisFeedback & { isPositive: boolean },
  { isPositive: boolean; currentText: string; generatedText: string; draft: EmailAnalysisDraft }
>(
  "messageWizard/sendFeedback",
  async (
    { isPositive, currentText, draft, generatedText },
    { getState, fulfillWithValue, rejectWithValue, signal }
  ) => {
    try {
      const {
        auth: { user },
      } = getState() as RootState;

      const { orgId = "", userId = "" } = user || {};

      const { platformDraftId, platformThreadId } = draft || {};

      const requestBody = {
        orgId,
        userId,
        platformThreadId: platformThreadId || "",
        draftPlatformMessageId: platformDraftId || "",
        draftText: currentText || "",
        rephrasedText: generatedText || "",
        feedback: isPositive,
        type: "message_wizard",
      };

      const response = await client.post<ApiResponseData<EmailAnalysisFeedback>>(
        `/services/orgs/${user?.orgId}/users/${user?.userId}/feedback`,
        requestBody,
        { signal }
      );

      return fulfillWithValue({
        ...response.data.payload,
        isPositive: requestBody.feedback,
      });
    } catch (error) {
      return rejectWithValue(null);
    }
  },
  {
    condition: (_, { getState }) => {
      const {
        auth: { user },
      } = getState() as RootState;

      return !!user;
    },
  }
);
